/**
 * La zona horaria de la campaña (pedido de Alan del 26/10/2026: «que siempre se pueda poner el horario, si es horario
 * argentino o de qué país»). La misma lista que el backend (`util/zonas.util.ts`); sin elegir, la de la marca
 * (Argentina). La franja propia, el último día y la fecha y hora a la que se programa se leen en la zona de la campaña:
 * antes la hora programada se tomaba del reloj del navegador de quien la cargaba, y la franja, en hora argentina.
 */

import { TZ_NEGOCIO } from "./estadisticas";

export interface ZonaHoraria {
  id: string;
  pais: string;
}

export const ZONAS: readonly ZonaHoraria[] = [
  { id: "America/Argentina/Buenos_Aires", pais: "Argentina" },
  { id: "America/Montevideo", pais: "Uruguay" },
  { id: "America/Sao_Paulo", pais: "Brasil (São Paulo)" },
  { id: "America/Asuncion", pais: "Paraguay" },
  { id: "America/Santiago", pais: "Chile" },
  { id: "America/La_Paz", pais: "Bolivia" },
  { id: "America/Lima", pais: "Perú" },
  { id: "America/Guayaquil", pais: "Ecuador" },
  { id: "America/Bogota", pais: "Colombia" },
  { id: "America/Caracas", pais: "Venezuela" },
  { id: "America/Panama", pais: "Panamá" },
  { id: "America/Mexico_City", pais: "México (Ciudad de México)" },
  { id: "America/New_York", pais: "Estados Unidos (Nueva York, Miami)" },
  { id: "Europe/Madrid", pais: "España" },
];

/** La de la marca: la que vale sin elegir otra. */
export const ZONA_MARCA = TZ_NEGOCIO;

export function paisDeZona(id: string | null | undefined): string {
  return ZONAS.find((z) => z.id === (id || ZONA_MARCA))?.pais || String(id);
}

const formatos = new Map<string, Intl.DateTimeFormat>();
function partes(t: number, zona: string): Record<string, number> {
  let f = formatos.get(zona);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone: zona, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
    formatos.set(zona, f);
  }
  return Object.fromEntries(f.formatToParts(new Date(t)).filter((p) => p.type !== "literal").map((p) => [p.type, Number(p.value)]));
}

/** Minutos que la zona le suma a UTC en ese instante (−180 en Argentina): la misma cuenta que `desfaseMin` del backend. */
function desfaseMin(t: number, zona: string): number {
  const p = partes(t, zona);
  const local = Date.UTC(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second);
  return Math.round((local - Math.floor(t / 1000) * 1000) / 60_000);
}

/** «GMT-3», «GMT-6», «GMT+1»: la diferencia con UTC en ese momento. */
export function gmtDe(zona: string, en: Date = new Date()): string {
  const m = desfaseMin(en.getTime(), zona);
  const h = Math.trunc(Math.abs(m) / 60);
  const min = Math.abs(m) % 60;
  return `GMT${m < 0 ? "-" : "+"}${h}${min ? `:${String(min).padStart(2, "0")}` : ""}`;
}

/** «México (Ciudad de México) · GMT-6». */
export function etiquetaZona(zona: string, en: Date = new Date()): string {
  return `${paisDeZona(zona)} · ${gmtDe(zona, en)}`;
}

const dos = (n: number) => String(n).padStart(2, "0");

/** Un instante → el valor de un `<input type="datetime-local">` en la hora de la zona («2026-10-27T10:00»). */
export function isoALocalEnZona(iso: string | Date, zona: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const p = partes(t, zona);
  return `${p.year}-${dos(p.month)}-${dos(p.day)}T${dos(p.hour % 24)}:${dos(p.minute)}`;
}

const LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/**
 * El valor de un `datetime-local` leído como hora de la zona → ISO. Inválido → null. Con un cambio de hora en el
 * medio, se corrige con el desfase del instante resultante (como `inicioDelDia` del backend).
 */
export function localEnZonaAIso(v: string, zona: string): string | null {
  const m = LOCAL_RE.exec(String(v || ""));
  if (!m) return null;
  const comoUtc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  if (Number.isNaN(comoUtc)) return null;
  const d1 = desfaseMin(comoUtc, zona);
  const t = comoUtc - d1 * 60_000;
  const d2 = desfaseMin(t, zona);
  return new Date(d2 === d1 ? t : comoUtc - d2 * 60_000).toISOString();
}

/** «27/10/26, 10:00» como `fechaHora`, pero en la hora de la zona y no en la del navegador. */
export function fechaHoraEnZona(iso: string | Date | null | undefined, zona: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("es-AR", { timeZone: zona, day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/** Rango del selector de la programación (desde dentro de 5 min hasta dentro de 30 días), en la hora de la zona. */
export function limitesProgramacionEnZona(ahora: Date, zona: string): { min: string; max: string } {
  return {
    min: isoALocalEnZona(new Date(ahora.getTime() + 5 * 60 * 1000), zona),
    max: isoALocalEnZona(new Date(ahora.getTime() + 30 * 24 * 60 * 60 * 1000), zona),
  };
}
